// client/src/components/admin/ModerationDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../axiosInstance';
import AppBarHeader from '../AppBarHeader';
import ImageModerationQueue from './ImageModerationQueue';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';

const ModerationDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [actionTaken, setActionTaken] = useState('none');
  const [statusFilter, setStatusFilter] = useState('pending');

  const CONTENT_TYPE_TABS = ['reviews', 'photos', 'comments'];
  const STATIC_TABS = ['image_queue'];

  useEffect(() => {
    axiosInstance.get('/reports/metrics').then(res => setMetrics(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (!STATIC_TABS.includes(statusFilter)) {
      fetchReports();
    }
  }, [isAdmin, navigate, statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (CONTENT_TYPE_TABS.includes(statusFilter)) {
        // Map tab value to targetType
        const targetTypeMap = { reviews: 'review', photos: 'photo', comments: 'comment' };
        params.targetType = targetTypeMap[statusFilter];
      } else {
        params.status = statusFilter;
      }
      const response = await axiosInstance.get('/reports', { params });
      setReports(response.data.reports || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleOpenDialog = (report) => {
    setSelectedReport(report);
    setResolution(report.resolution || '');
    setActionTaken(report.actionTaken || 'none');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
    setResolution('');
    setActionTaken('none');
  };

  const handleResolveReport = async (status) => {
    if (!selectedReport) return;

    try {
      await axiosInstance.patch(`/reports/${selectedReport._id}`, {
        status,
        resolution,
        actionTaken
      });
      handleCloseDialog();
      fetchReports();
    } catch (err) {
      console.error('Error updating report:', err);
      alert(err.response?.data?.error || 'Failed to update report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report? This cannot be undone.')) return;

    try {
      await axiosInstance.delete(`/reports/${reportId}`);
      fetchReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      alert(err.response?.data?.error || 'Failed to delete report');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewing': return 'info';
      case 'resolved': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const getReasonLabel = (reason) => {
    return reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Admin privileges required.</Alert>
      </Box>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Moderation Dashboard
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          ← Back to Admin Dashboard
        </Button>

        {metrics && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`Pending: ${metrics.pendingCount}`} color="warning" />
            <Chip label={`In Review: ${metrics.reviewingCount}`} />
            <Chip label={`Resolved Today: ${metrics.resolvedToday}`} color="success" />
            {metrics.avgReviewTimeHours !== null && (
              <Chip label={`Avg Review Time: ${metrics.avgReviewTimeHours}h`} />
            )}
            {metrics.oldestPendingAgeHours !== null && (
              <Chip
                label={`Oldest Pending: ${metrics.oldestPendingAgeHours}h`}
                color={metrics.oldestPendingAgeHours > 48 ? 'error' : 'default'}
                // 48 = MODERATION_ALERT_AGE_HOURS — keep in sync with services/badgeConfig.js
              />
            )}
          </Box>
        )}

        <Tabs value={statusFilter} onChange={(e, newValue) => setStatusFilter(newValue)} sx={{ mb: 3 }}>
          <Tab label="Pending" value="pending" />
          <Tab label="Reviewing" value="reviewing" />
          <Tab label="Resolved" value="resolved" />
          <Tab label="Dismissed" value="dismissed" />
          <Tab label="Image Queue" value="image_queue" />
          <Tab label="Reviews" value="reviews" />
          <Tab label="Photos" value="photos" />
          <Tab label="Comments" value="comments" />
        </Tabs>

        {statusFilter === 'image_queue' ? (
          <ImageModerationQueue />
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : reports.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No {statusFilter} reports
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Reported By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report._id}>
                        <TableCell>
                          {new Date(report.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={report.targetType} size="small" />
                        </TableCell>
                        <TableCell>{getReasonLabel(report.reason)}</TableCell>
                        <TableCell>{report.reportedBy?.username || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip
                            label={report.status}
                            color={getStatusColor(report.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => handleOpenDialog(report)}
                            sx={{ mr: 1 }}
                          >
                            Review
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleDeleteReport(report._id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      {/* Review Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Review Report</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Type:</strong> {selectedReport.targetType}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Reason:</strong> {getReasonLabel(selectedReport.reason)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Description:</strong> {selectedReport.description || 'None provided'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Reported by:</strong> {selectedReport.reportedBy?.username}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {new Date(selectedReport.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Target ID:</strong> {selectedReport.targetId}
              </Typography>

              <FormControl fullWidth sx={{ mt: 3, mb: 2 }}>
                <InputLabel>Action Taken</InputLabel>
                <Select
                  value={actionTaken}
                  onChange={(e) => setActionTaken(e.target.value)}
                  label="Action Taken"
                >
                  <MenuItem value="none">No Action</MenuItem>
                  <MenuItem value="warning">Warning Issued</MenuItem>
                  <MenuItem value="content_removed">Content Removed</MenuItem>
                  <MenuItem value="user_banned">User Banned</MenuItem>
                  <MenuItem value="thread_locked">Thread Locked</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Resolution Notes"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Add notes about how this was resolved..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={() => handleResolveReport('dismissed')} color="warning">
            Dismiss
          </Button>
          <Button onClick={() => handleResolveReport('resolved')} variant="contained" color="success">
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModerationDashboard;
